import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import firebase from '../../utilities/firebase';
import RequestManager from '../../utilities/requestManager';
import { StateManager } from '../../utilities/stateManager';
import { StackedDialog } from '../../utilities/ModalStackContext';
import { useSoldLock } from './SoldLock';

const BUCKET = 'skyway-dev-373d5.appspot.com';

// A status code/string is "terminal" once it can no longer progress.
const SIGNED = ['signed', 'signed and downloaded', 'downloaded'];
const TERMINAL = [...SIGNED, 'cancelled', 'declined', 'expired'];

const isSigned = (status = '') => SIGNED.includes(status);
const isTerminal = (status = '') => TERMINAL.includes(status);

// Map a record's status to a display chip { label, color }.
const statusChip = (status = '') => {
  if (isSigned(status)) return { label: 'Signed', color: 'success' };
  if (status === 'cancelled') return { label: 'Cancelled', color: 'default' };
  if (status === 'declined') return { label: 'Declined', color: 'error' };
  if (status === 'expired') return { label: 'Expired', color: 'warning' };
  return { label: 'Awaiting signature', color: 'info' };
};

// One milestone row in a signer's progress.
function Step({ done, label, at }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {done
        ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
        : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />}
      <Typography variant="body2" sx={{ color: done ? 'text.primary' : 'text.secondary', flex: 1 }}>
        {label}
      </Typography>
      {at && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{at}</Typography>}
    </Box>
  );
}

function SignerProgress({ signer, declined }) {
  const fmt = (v) => (v ? new Date(v).toLocaleString() : null);
  const emailedOn = fmt(signer.emailed_on);
  const viewedOn = fmt(signer.viewed_on);
  const signedOn = fmt(signer.signed_on);
  const declinedOn = fmt(signer.declined_on);
  return (
    <Box sx={{ pl: 0.5, py: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
        {signer.name || signer.email || 'Signer'}
        {signer.email && signer.name ? <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>{signer.email}</Typography> : null}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Step done={signer.emailed} label="Email sent" at={emailedOn} />
        <Step done={signer.viewed} label="Opened document" at={viewedOn} />
        {signer.declined
          ? <Step done label="Declined to sign" at={declinedOn} />
          : <Step done={signer.signed} label="Signed" at={signedOn} />}
      </Box>
    </Box>
  );
}

export default function SignatureStatusDialog({ open, onClose, car = {}, type, label, table = 'cars' }) {
  const stock = car.stock;
  const [records, setRecords] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null); // document_uuid being cancelled
  // The dialog is portaled out of the SoldLock guard, so hide the one write here.
  const soldLocked = useSoldLock();

  // Load the request history for this packet type, newest first. For any
  // still-active request, refresh authoritative per-signer state from
  // SignRequest (the webhook updates this too, but a live fetch guarantees the
  // dialog is current the moment it opens).
  const load = React.useCallback(async () => {
    if (!stock || !type) return;
    setLoading(true);
    try {
      const snap = await firebase.firestore()
        .collection(`${table}/${stock}/signatures`)
        .where('type', '==', type)
        .get();
      let recs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      recs.sort((a, b) => (b.sentAt || b.updatedAt || '').localeCompare(a.sentAt || a.updatedAt || ''));

      recs = await Promise.all(recs.map(async (rec) => {
        if (isTerminal(rec.status) || !rec.document_uuid) return rec;
        const live = await RequestManager.get({
          function: 'getSignatureSessionStatus',
          variables: { document_uuid: rec.document_uuid },
        });
        if (live && live.status === 'complete') {
          return { ...rec, signers: live.signers || rec.signers, livePdf: live.pdf || null };
        }
        return rec;
      }));
      setRecords(recs);
    } catch (e) {
      console.log('Failed to load signature status', e);
    }
    setLoading(false);
  }, [stock, type]);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  const cancelRequest = async (rec) => {
    setBusyId(rec.document_uuid);
    const res = await RequestManager.post({
      function: 'cancelSignatureSession',
      variables: { document_uuid: rec.document_uuid, table, id: stock, type },
    });
    if (res && res.status === 'complete') {
      StateManager.setAlertAndOpen('Signature request cancelled.', 'success');
      await load();
    } else {
      StateManager.setAlertAndOpen('Could not cancel the request.', 'error');
    }
    setBusyId(null);
  };

  const viewSigned = async (rec) => {
    try {
      if (rec.signedDocPath) {
        const url = await firebase.app().storage(BUCKET).ref(rec.signedDocPath).getDownloadURL();
        window.open(url, '_blank');
      } else if (rec.livePdf) {
        window.open(rec.livePdf, '_blank');
      }
    } catch (e) {
      StateManager.setAlertAndOpen('Could not open the signed document.', 'error');
    }
  };

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        {label || 'Signature'} status
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && records.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
        ) : records.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
            No packets have been sent for signature yet.
          </Typography>
        ) : (
          records.map((rec, i) => {
            const chip = statusChip(rec.status);
            const active = !isTerminal(rec.status);
            const signers = rec.signers || [];
            return (
              <Box key={rec.id} sx={{
                border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2, mb: i === records.length - 1 ? 0 : 2,
                bgcolor: (t) => isSigned(rec.status) ? alpha(t.palette.success.main, 0.05) : 'transparent',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip size="small" label={chip.label} color={chip.color} variant={isSigned(rec.status) ? 'filled' : 'outlined'} />
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Sent {rec.sentAt || rec.updatedAt || '—'}
                  </Typography>
                </Box>
                {rec.name && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>{rec.name}</Typography>}

                {signers.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>No signer details available.</Typography>
                ) : signers.map((s, si) => (
                  <React.Fragment key={s.email || si}>
                    {si > 0 && <Divider sx={{ my: 1 }} />}
                    <SignerProgress signer={s} />
                  </React.Fragment>
                ))}

                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                  {isSigned(rec.status) && (
                    <Button size="small" variant="outlined" color="success" startIcon={<OpenInNewIcon />} onClick={() => viewSigned(rec)}>
                      View signed document
                    </Button>
                  )}
                  {active && !soldLocked && (
                    <Button
                      size="small" variant="outlined" color="error"
                      startIcon={busyId === rec.document_uuid ? <CircularProgress size={14} /> : <CancelIcon />}
                      disabled={busyId === rec.document_uuid}
                      onClick={() => cancelRequest(rec)}
                    >
                      Cancel request
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </StackedDialog>
  );
}
